import React, { useEffect, useMemo, useState } from "react";
import "moment-timezone";
import moment from "moment";
import { useTranslation } from "react-i18next";
import AppPaginatedTable from "components/ui/AppPaginatedTable";

const getStatusBadgeStyle = (status) => {
  const newStatus = status?.toUpperCase();

  const statusStyles = {
    PENDING: {
      backgroundColor: "#FFF9E6",
      color: "#FF8C00",
    },
    COMPLETED: {
      backgroundColor: "#E6F7E6",
      color: "#22C55E",
    },
    COMPLETE: {
      backgroundColor: "#E6F7E6",
      color: "#22C55E",
    },
    ON_THE_WAY: {
      backgroundColor: "#E6F2FF",
      color: "#3B82F6",
    },
    SCHEDULED: {
      backgroundColor: "#E6F7E6",
      color: "#22C55E",
    },
    REJECTED: {
      backgroundColor: "#FFE6E6",
      color: "#EF4444",
    },
    PREPARING: {
      backgroundColor: "#FFF4E6",
      color: "#F97316",
    },
    CANCELLED: {
      backgroundColor: "#FFE6E6",
      color: "#EF4444",
    },
    CANCELED: {
      backgroundColor: "#FFE6E6",
      color: "#EF4444",
    },
  };

  return (
    statusStyles[newStatus] || {
      backgroundColor: "#FFF9E6",
      color: "#FF8C00",
    }
  );
};

const formatStatusLabel = (status) => {
  const normalized = String(status || "").toUpperCase();
  if (normalized === "ON_THE_WAY") return "On the Way";
  if (normalized === "COMPLETE") return "Completed";
  return String(status || "")
    .toLowerCase()
    .replaceAll("_", " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const OnlineOrderTable = ({ orders = { data: [] }, searchTerm = "" }) => {
  const { t, i18n } = useTranslation();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const filteredOrders = useMemo(() => {
    const raw = orders?.data || [];
    const term = searchTerm?.trim().toLowerCase();
    if (!term) return raw;

    return raw.filter((item) => {
      const orderNumber = String(item?.orderNumber ?? "").toLowerCase();
      const orderNumberNorm = orderNumber.replace(/[^a-z0-9]/g, "");
      const termNorm = term.replace(/[^a-z0-9]/g, "");

      const storeName = String(
        item?.storeInfo?.baseLanguage?.name ?? ""
      ).toLowerCase();
      const phone = String(item?.userInfo?.phoneNumber ?? "").toLowerCase();
      const typeName =
        i18n.language === "ar"
          ? String(item?.orderType?.AR?.name ?? "").toLowerCase()
          : String(item?.orderType?.EN?.name ?? "").toLowerCase();
      const payment = String(item?.paymentType ?? "").toLowerCase();

      return (
        orderNumber.includes(term) ||
        (termNorm.length > 0 && orderNumberNorm.includes(termNorm)) ||
        storeName.includes(term) ||
        phone.includes(term) ||
        typeName.includes(term) ||
        payment.includes(term)
      );
    });
  }, [orders?.data, searchTerm, i18n.language]);

  useEffect(() => {
    setPage(1);
  }, [searchTerm]);

  const columns = [
    {
      title: t("Number"),
      dataIndex: "orderNumber",
      align: "initial",
      key: "orderNumber",
    },
    {
      title: t("Price"),
      dataIndex: "totalOrderPrice",
      align: "initial",
      key: "totalOrderPrice",
    },
    {
      title: t("Status"),
      dataIndex: "orderStatus",
      align: "initial",
      key: "orderStatus",
      render: (val) => (
        <span
          className="inline-flex min-w-[98px] items-center justify-center rounded-full px-4 py-2 text-sm font-medium"
          style={getStatusBadgeStyle(val)}
        >
          {formatStatusLabel(val)}
        </span>
      ),
    },
    {
      title: t("Verified"),
      dataIndex: "orderCheckoutOnlinePaymentMetadata",
      align: "initial",
      key: "orderCheckoutOnlinePaymentMetadata",
      render: (val, row) => {
        if (row?.paymentType !== "CREDIT_CARD") return t("N/a");
        return row?.orderCheckoutOnlinePaymentMetadata?.data?.status ===
          "VERIFIED"
          ? t("yes")
          : t("no");
      },
    },
    {
      title: t("Order Placed Time"),
      dataIndex: "orderPlacedTime",
      align: "initial",
      key: "orderPlacedTime",
      render: (val) =>
        val != null
          ? moment.unix(val).tz("UTC").format("DD/MM/YYYY hh:mm A")
          : "",
    },

    {
      title: t("Store"),
      dataIndex: "storeInfo",
      width: "25%",
      render: (val) => val?.baseLanguage?.name,
      key: "storeInfo",
    },
    {
      title: t("Payment Method"),
      dataIndex: "paymentType",
      align: "initial",
      key: "paymentType",
      render: (val) => t(val),
    },
  ];

  return (
    <div className="w-full">
      <AppPaginatedTable
        columns={columns}
        data={filteredOrders}
        clientSidePagination={true}
        page={page}
        pageSize={pageSize}
        total={filteredOrders.length}
        onPageChange={(nextPage, nextPageSize) => {
          setPage(nextPage);
          setPageSize(nextPageSize);
        }}
      />
    </div>
  );
};

export default OnlineOrderTable;
